import type { HumanReadyReason } from './types.js';

let ready = false;
let readyReason: HumanReadyReason | null = null;
let gateEnabled = true;
let installedAtMs = 0;
const subscribers = new Set<(reason: HumanReadyReason) => void>();

export function isHumanGateEnabled(): boolean {
  return gateEnabled;
}

/** Test / kill-switch helper. Does not emit analytics. */
export function setHumanGateEnabled(enabled: boolean): void {
  gateEnabled = enabled;
}

export function isHumanReady(): boolean {
  return ready;
}

export function getHumanReadyReason(): HumanReadyReason | null {
  return readyReason;
}

/** Milliseconds since `installHumanGate` (0 if never installed). */
export function getHumanGateAgeMs(now: number = Date.now()): number {
  if (installedAtMs <= 0) return 0;
  return Math.max(0, now - installedAtMs);
}

export function markHumanGateInstalled(at: number = Date.now()): void {
  if (installedAtMs <= 0) {
    installedAtMs = at;
  }
}

/**
 * Open the gate once. Returns true if this call flipped ready.
 * Idempotent — later calls are no-ops.
 */
export function markHumanReady(reason: HumanReadyReason): boolean {
  if (ready) return false;
  ready = true;
  readyReason = reason;
  for (const subscriber of [...subscribers]) {
    subscriber(reason);
  }
  return true;
}

export function subscribeHumanReady(
  callback: (reason: HumanReadyReason) => void,
): () => void {
  if (ready && readyReason) {
    callback(readyReason);
    return () => {};
  }
  subscribers.add(callback);
  return () => {
    subscribers.delete(callback);
  };
}

/** Resolves when the gate is open (immediately if already ready / disabled). */
export function whenHumanReady(): Promise<HumanReadyReason> {
  if (ready && readyReason) {
    return Promise.resolve(readyReason);
  }
  if (!gateEnabled) {
    markHumanReady('disabled');
    return Promise.resolve('disabled');
  }
  return new Promise((resolve) => {
    const unsubscribe = subscribeHumanReady((reason) => {
      unsubscribe();
      resolve(reason);
    });
  });
}

/** Reset singleton state between unit tests. */
export function resetHumanGateForTests(): void {
  ready = false;
  readyReason = null;
  gateEnabled = true;
  installedAtMs = 0;
  subscribers.clear();
}
